import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { CaretDown, CaretRight, Eye, EyeSlash } from "@phosphor-icons/react";

const MODE_LABEL = {
  daily: "NK Daily",
  ladder: "NK Ladder",
  saturday: "Saturday Challenge",
  championship: "Championship",
  duel: "NK Duel",
  survival: "NK Survival",
};

export default function LoadedQuestions({ mode }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [showAnswers, setShowAnswers] = useState(false);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get(`/admin/questions/loaded/${mode}`);
        if (alive) setData(r.data);
      } catch (_) { /* admin without questions perm */ }
    };
    tick();
    const id = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [mode]);

  if (!data) return null;
  if (!data.found) {
    return (
      <section className="nk-card p-4 border-l-2 border-nk-line/60">
        <p className="text-sm text-nk-mute">
          <span className="text-nk-text">{MODE_LABEL[mode] || mode}</span> · no scheduled event yet.
        </p>
      </section>
    );
  }

  return (
    <section className="nk-card p-4" data-testid={`loaded-questions-${mode}`}>
      <header className="flex items-center justify-between gap-2 mb-3">
        <button onClick={() => setOpen(!open)} className="flex items-center gap-2 text-left">
          {open ? <CaretDown size={14} /> : <CaretRight size={14} />}
          <div>
            <p className="eyebrow">{MODE_LABEL[mode] || mode} · loaded</p>
            <p className="font-display text-base">
              {data.question_count != null
                ? `${data.question_count} questions for ${data.state}`
                : `${(data.matches?.length || data.runs?.length || 0)} recent ${mode === "duel" ? "matches" : "runs"}`}
            </p>
            {data.scheduled_for && (
              <p className="text-[11px] text-nk-dim font-mono">
                Scheduled: {new Date(data.scheduled_for).toLocaleString()}
              </p>
            )}
            {data.sponsor_name && (
              <p className="text-[11px] text-nk-gold">
                Sponsor: {data.sponsor_name}{data.sponsor_owned_questions && " · sponsor-owned slate"}
              </p>
            )}
          </div>
        </button>
        <button onClick={() => setShowAnswers(!showAnswers)}
                data-testid={`toggle-answers-${mode}`}
                className="inline-flex items-center gap-1.5 text-[11px] text-nk-mute hover:text-nk-text border border-nk-line hover:border-nk-accent px-2 py-1 rounded-sm">
          {showAnswers ? <EyeSlash size={11} /> : <Eye size={11} />}
          {showAnswers ? "Hide answers" : "Reveal answers"}
        </button>
      </header>

      {open && (
        <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
          {data.questions && data.questions.map((q, i) => (
            <QuestionRow key={q.qid + "-" + i} q={q} index={i + 1} showAnswers={showAnswers} />
          ))}
          {data.matches && data.matches.map((m) => (
            <div key={m.match_id} className="nk-card p-3">
              <p className="text-[11px] text-nk-dim uppercase tracking-wider mb-2">
                Match · {m.state} · {new Date(m.created_at).toLocaleString()}
              </p>
              <div className="space-y-1">
                {m.questions.map((q, i) => (
                  <QuestionRow key={q.qid + "-" + i} q={q} index={i + 1} showAnswers={showAnswers} compact />
                ))}
              </div>
            </div>
          ))}
          {data.runs && data.runs.map((r) => (
            <div key={r.run_id} className="nk-card p-3">
              <p className="text-[11px] text-nk-dim uppercase tracking-wider mb-2">
                Run · {r.state} · {new Date(r.started_at).toLocaleString()}
              </p>
              <div className="space-y-1">
                {r.questions_seen.map((q, i) => (
                  <QuestionRow key={q.qid + "-" + i} q={q} index={i + 1} showAnswers={showAnswers} compact />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function QuestionRow({ q, index, showAnswers, compact }) {
  if (q.missing) {
    return (
      <div className="text-xs text-nk-bad px-2 py-1">
        Q{index}: missing from bank ({q.qid})
      </div>
    );
  }
  return (
    <div className={`rounded-sm ${compact ? "px-2 py-1" : "p-2.5 bg-nk-bg/40 border border-nk-line/50"}`}>
      <p className="text-sm text-nk-text">
        <span className="text-nk-dim font-mono mr-2">Q{index}.</span>
        {q.body}
      </p>
      <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
        {q.options.map((o, i) => (
          <span key={i} className={
            showAnswers && i === q.answer_index ? "text-nk-accent font-medium" : "text-nk-mute"
          }>
            {String.fromCharCode(65 + i)}. {o}
            {showAnswers && i === q.answer_index && " ✓"}
          </span>
        ))}
      </div>
      <p className="text-[10px] text-nk-dim mt-1 uppercase tracking-wider">
        {q.category} · {q.difficulty}{q.country_tag && q.country_tag !== "ALL" ? ` · ${q.country_tag}` : ""}
      </p>
    </div>
  );
}
